// CR085: Flyer Truth Table - Terminal step showing all page outcomes
// CR086: Added real-time polling while OCR is running + fixed OCR text mapping
// CR460: Made attempt-aware with DataSourceBanner + step attempt selectors
// CR: Added validated payload unwrapping utilities for proper field access
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import {
  BarChart3, CheckCircle, XCircle, Eye, FileText,
  Search, Zap, Image, AlertCircle, Filter, Loader2, RefreshCw, Info, BrainCircuit, Target
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import type { Database, Json } from "@/integrations/supabase/types";
import { TruthTablePage, TruthTableSummary, PageOutcome } from "./types";
import { FlyerEvidencePanel } from "./FlyerEvidencePanel";
import { DataSourceBanner } from "./DataSourceBanner";
import { useStepAttempts } from "./useStepAttempts";
import { getValidatedDeal } from "@/lib/batch-runs/validatedPayloadUtils";
import { getOcrEngineLabel } from "@/lib/batch-runs/ocrEngine";
import { deriveCandidateResultLabel, deriveTriggeredSignals } from "./signalDebug";

interface FlyerTruthTableProps {
  batchRunId: string;
  ocrStepStatus?: string; // "RUNNING" | "COMPLETE" | etc
  /** When true, renders without Card wrapper, hides progress bar & duplicate metadata */
  embedded?: boolean;
  /** When true, hides the OCR progress banner */
  hideProgress?: boolean;
  /** Hide attempt selector/source references (S1.3 compact mode) */
  hideDataSourceBanner?: boolean;
  /** Hide AI-related table references (S1.3 compact mode) */
  hideAiReferences?: boolean;
}

const normalizeFlyerLabel = (...candidates: Array<string | null | undefined>) => {
  for (const candidate of candidates) {
    const raw = (candidate ?? "").trim();
    if (!raw) continue;
    if (raw.toLowerCase() === "unknown") continue;
    // If a storage-like path sneaks in, keep only the filename segment.
    const filename = raw.split("/").pop() ?? raw;
    return filename;
  }
  return "unknown";
};

const DEFAULT_OUTCOME_FILTER = "OCR_COMPLETE_SET";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type RpcCr460Row = Database["public"]["Functions"]["rpc_cr460_page_lifecycle"]["Returns"][number];
type LifecycleRow = RpcCr460Row & {
  candidate_score_breakdown?: Json | null;
  candidate_reason_codes?: string[] | null;
  candidate_for_ai?: boolean | null;
};

/** Compact summary for use in accordion headers */
export function useTruthTableSummary(pages: TruthTablePage[]) {
  return useMemo(() => {
    if (pages.length === 0) return null;
    return {
      totalPages: pages.length,
      ocrPages: pages.filter(p => p.ocrComplete).length,
      candidatePages: pages.filter(p => p.isCandidate).length,
      dealsCreated: pages.filter(p => p.outcome === "DEAL_CREATED").length,
      discardedPages: pages.filter(p => p.outcome === "OCR_COMPLETE" || p.outcome === "OCR_FAILED").length,
    };
  }, [pages]);
}

export function FlyerTruthTable({
  batchRunId,
  ocrStepStatus,
  embedded = false,
  hideProgress = false,
  hideDataSourceBanner = false,
  hideAiReferences = false,
}: FlyerTruthTableProps) {
  // CR460: Step attempt awareness
  const stepAttempts = useStepAttempts(batchRunId);
  const navigate = useNavigate();
  const [pages, setPages] = useState<TruthTablePage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState<TruthTablePage | null>(null);
  const [evidencePanelOpen, setEvidencePanelOpen] = useState(false);
  const [outcomeFilter, setOutcomeFilter] = useState<string>(DEFAULT_OUTCOME_FILTER);
  const [ocrProgress, setOcrProgress] = useState<{ processed: number; total: number; status: string } | null>(null);
  const [reviewQueueOpen, setReviewQueueOpen] = useState(false);
  const [manualLabels, setManualLabels] = useState<Record<string, "CANDIDATE" | "NOT_CANDIDATE">>({});

  // CR095: Render step status for visibility
  const [renderStepStatus, setRenderStepStatus] = useState<{
    status: string | null;
    skippedReason: string | null;
    blockedReason: string | null;
  }>({ status: null, skippedReason: null, blockedReason: null });

  const [configSnapshot, setConfigSnapshot] = useState<{
    ocr_engine_selected?: string;
    ocr_engine_used?: string;
    execution_mode?: "CLIENT" | "EDGE";
    fallback_used?: boolean;
    fallback_reason?: string | null;
    candidate_threshold?: number;
    min_ocr_score?: number;
    signals_count?: number;
    signals_hash?: string; // CR088: Added for audit
    signals_source_path?: string; // CR088: Shows where signals came from
    config_source?: string;
  } | null>(null);
  const [showProofDialog, setShowProofDialog] = useState(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const merchantNameByKeyRef = useRef<Map<string, string>>(new Map());
  const labelStorageKey = useMemo(() => `cr553_learning_labels_${batchRunId}`, [batchRunId]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(labelStorageKey);
      if (!raw) {
        setManualLabels({});
        return;
      }
      const parsed = JSON.parse(raw) as Record<string, "CANDIDATE" | "NOT_CANDIDATE">;
      setManualLabels(parsed ?? {});
    } catch {
      setManualLabels({});
    }
  }, [labelStorageKey]);

  const setManualLabel = useCallback((pageId: string, label: "CANDIDATE" | "NOT_CANDIDATE") => {
    setManualLabels((prev) => {
      const next = { ...prev, [pageId]: label };
      try {
        localStorage.setItem(labelStorageKey, JSON.stringify(next));
      } catch {
        // ignore persistence errors
      }
      return next;
    });
  }, [labelStorageKey]);

  const clearManualLabel = useCallback((pageId: string) => {
    setManualLabels((prev) => {
      if (!(pageId in prev)) return prev;
      const next = { ...prev };
      delete next[pageId];
      try {
        localStorage.setItem(labelStorageKey, JSON.stringify(next));
      } catch {
        // ignore persistence errors
      }
      return next;
    });
  }, [labelStorageKey]);

  const hasAnchorText = useCallback((page: TruthTablePage) => {
    const text = `${page.ocrText ?? ""} ${(page.triggeredSignals ?? []).join(" ")}`.toLowerCase();
    return /(gift\s*card|prepaid|mastercard|visa\s*card|gift\s*cards)/.test(text);
  }, []);

  const hasOfferStructureText = useCallback((page: TruthTablePage) => {
    const text = `${page.ocrText ?? ""} ${(page.triggeredSignals ?? []).join(" ")}`.toLowerCase();
    return /(spend|buy|purchase|with the purchase of).{0,120}(get|receive|earn)/s.test(text)
      || /(when you buy|when you purchase|get \$|receive \$|earn \$)/.test(text);
  }, []);

  const getPageProbability = useCallback((page: TruthTablePage): number => {
    if (page.aiDealExtracted) return 0.98;
    if (page.outcome === "OCR_FAILED" || page.outcome === "RENDER_FAILED") return 0.01;
    const threshold = page.signalThreshold ?? 12;
    const score = page.signalScore ?? 0;
    const hasAnchor = hasAnchorText(page);
    const hasStructure = hasOfferStructureText(page);
    const normalizedScore = threshold > 0 ? score / threshold : 0;

    // Heuristic confidence for operator guidance only (does not decide candidate status).
    // Keep this conservative when anchor/structure gates are missing so percentages remain intuitive.
    let raw = 0.1;
    raw += Math.min(Math.max(normalizedScore, 0), 2) * 0.25; // score contribution, capped
    if (hasAnchor) raw += 0.3;
    if (hasStructure) raw += 0.26;
    if (page.isCandidate) raw += 0.05;

    if (!hasAnchor) raw = Math.min(raw, 0.42);
    if (hasAnchor && !hasStructure) raw = Math.min(raw, 0.58);
    if (!page.isCandidate && score < threshold) raw = Math.min(raw, 0.69);

    return Math.max(0.01, Math.min(0.99, raw));
  }, [hasAnchorText, hasOfferStructureText]);

  const getDecisionSource = useCallback((page: TruthTablePage): "Rules" | "Hybrid" | "Model" => {
    if (page.isCandidate) return "Hybrid";
    return "Rules";
  }, []);

  const getTopReasons = useCallback((page: TruthTablePage): string[] => {
    const reasons: string[] = [];
    if (hasAnchorText(page)) reasons.push("Gift-card anchor");
    if (hasOfferStructureText(page)) reasons.push("Offer structure");
    if ((page.signalScore ?? 0) >= (page.signalThreshold ?? 12)) reasons.push("Score >= threshold");
    if (!reasons.length && (page.signalScore ?? 0) < (page.signalThreshold ?? 12)) reasons.push("Below threshold");
    return reasons.slice(0, 3);
  }, [hasAnchorText, hasOfferStructureText]);

  const hydrateMerchantDisplayNames = useCallback(async (keys: Array<string | null | undefined>) => {
    const normalizedKeys = Array.from(
      new Set(
        keys
          .map((k) => (k ?? "").trim())
          .filter((k) => k.length > 0 && k.toLowerCase() !== "unknown"),
      ),
    );
    if (normalizedKeys.length === 0) return;

    const missingKeys = normalizedKeys.filter((key) => !merchantNameByKeyRef.current.has(key));
    if (missingKeys.length === 0) return;

    const idCandidates = missingKeys.filter((key) => UUID_PATTERN.test(key));
    const keyCandidates = missingKeys.filter((key) => !UUID_PATTERN.test(key));

    const requests = [];

    if (keyCandidates.length > 0) {
      requests.push(
        supabase
          .from("merchants")
          .select("id, merchant_key, display_name")
          .in("merchant_key", keyCandidates),
      );
    }
    if (idCandidates.length > 0) {
      requests.push(
        supabase
          .from("merchants")
          .select("id, merchant_key, display_name")
          .in("id", idCandidates),
      );
    }

    if (requests.length === 0) return;
    const responses = await Promise.all(requests);
    for (const response of responses) {
      if (response.error) {
        console.warn("[FlyerTruthTable] Failed to resolve merchant display names:", response.error.message);
        continue;
      }
      for (const row of response.data ?? []) {
        const id = String(row.id ?? "").trim();
        const key = String(row.merchant_key ?? "").trim();
        const display = String(row.display_name ?? "").trim();
        const resolved = display || key || id;
        if (id) merchantNameByKeyRef.current.set(id, resolved);
        if (key) merchantNameByKeyRef.current.set(key, resolved);
      }
    }
  }, []);

  // CR461: Fetch truth table via rpc_cr460_page_lifecycle keyed by selected attempt IDs
  const fetchTruthTable = useCallback(async (isPolling = false) => {
    if (!isPolling) setLoading(true);
    try {
      // Use the RPC when we have attempt IDs, fall back to legacy fetch otherwise
      const ocrAttemptId = stepAttempts.selectedOcrAttemptId;
      const aiAttemptId = stepAttempts.selectedAiAttemptId;

      // Fetch page lifecycle from RPC
      const rpcArgs: { p_batch_run_id: string; p_ocr_attempt_id?: string; p_ai_attempt_id?: string } = { p_batch_run_id: batchRunId };
      if (ocrAttemptId) rpcArgs.p_ocr_attempt_id = ocrAttemptId;
      if (aiAttemptId) rpcArgs.p_ai_attempt_id = aiAttemptId;

      const [{ data: lifecycleRows, error: rpcError }, { data: ocrAttempts }, { data: renderStep }, { data: stagingItems }, { data: truthRows }] = await Promise.all([
        supabase.rpc("rpc_cr460_page_lifecycle", rpcArgs),
        supabase
          .from("cr075_batch_run_step_attempts")
          .select("diagnostics_json, status, config_json")
          .eq("batch_run_id", batchRunId)
          .eq("step_key", "S1_OCR")
          .order("created_at", { ascending: false })
          .limit(1),
        supabase
          .from("cr075_batch_run_steps")
          .select("status, skipped_reason, config_json")
          .eq("batch_run_id", batchRunId)
          .eq("step_key", "S1_RENDER")
          .maybeSingle(),
        supabase
          .from("cr075_staging_deal_items")
          .select("id, merchant_key, evidence_json")
          .eq("batch_run_id", batchRunId)
          .eq("source_type", "FLYER"),
        supabase
          .from("cr075_flyer_page_truth")
          .select("id, flyer_merchant, staging_item_id")
          .eq("batch_run_id", batchRunId),
      ]);

      const stagingLabelById = new Map<string, { merchantKey: string; flyerName: string | null }>();
      const stagingMerchantByPath = new Map<string, string>();
      const stagingMerchantByPage = new Map<number, string>();
      const truthRowById = new Map<string, { flyerMerchant: string | null; stagingItemId: string | null }>();
      for (const item of stagingItems ?? []) {
        const evidence = (item.evidence_json as Record<string, unknown> | null) ?? {};
        const flyerPages = Array.isArray(evidence.flyer_pages) ? evidence.flyer_pages as Array<Record<string, unknown>> : [];
        stagingLabelById.set(item.id, {
          merchantKey: item.merchant_key,
          flyerName: (evidence.flyer_filename as string) ?? (evidence.flyer_name as string) ?? null,
        });
        for (const fp of flyerPages) {
          const pageNumber = Number(fp?.page_number);
          const storagePath = String(fp?.storage_path ?? "").trim();
          if (storagePath.length > 0 && !stagingMerchantByPath.has(storagePath)) {
            stagingMerchantByPath.set(storagePath, item.merchant_key);
          }
          if (!Number.isFinite(pageNumber)) continue;
          // Keep first mapping for a page number to avoid noisy overrides.
          if (!stagingMerchantByPage.has(pageNumber)) {
            stagingMerchantByPage.set(pageNumber, item.merchant_key);
          }
        }
      }
      for (const truth of truthRows ?? []) {
        truthRowById.set(truth.id, {
          flyerMerchant: truth.flyer_merchant,
          stagingItemId: truth.staging_item_id,
        });
      }
      await hydrateMerchantDisplayNames([
        ...(stagingItems ?? []).map((item) => item.merchant_key),
        ...(lifecycleRows ?? []).map((row) => (row as Record<string, unknown>)?.flyer_merchant as string | null | undefined),
        ...(truthRows ?? []).map((row) => row.flyer_merchant),
      ]);

      if (renderStep) {
        const configJson = renderStep.config_json as { blocked_reason?: string } | null;
        setRenderStepStatus({
          status: renderStep.status,
          skippedReason: renderStep.skipped_reason,
          blockedReason: configJson?.blocked_reason || null,
        });
      }

      const ocrAttempt = ocrAttempts?.[0] as Record<string, unknown> | undefined;
      const ocrDiagnostics = ocrAttempt?.diagnostics_json as Record<string, unknown> | undefined;

      // Update progress tracking
      if (ocrDiagnostics) {
        setOcrProgress({
          processed: (ocrDiagnostics.pages_processed as number) ?? 0,
          total: (ocrDiagnostics.total_pages as number) ?? 0,
          status: (ocrDiagnostics.status as string) ?? (ocrAttempt?.status as string) ?? "UNKNOWN",
        });

        // Update config snapshot - prefer dedicated config_json, fallback to diagnostics
        const configSrc = (ocrAttempt?.config_json as Record<string, unknown>) ??
          (ocrDiagnostics.config_snapshot as Record<string, unknown>);
        if (configSrc) {
          setConfigSnapshot(configSrc as typeof configSnapshot);
        }
      }

      // If RPC returned data, use it
      if (!rpcError && lifecycleRows && lifecycleRows.length > 0) {
        const signalThreshold = lifecycleRows[0]?.candidate_threshold ?? 12;

        const typedLifecycleRows = (lifecycleRows ?? []) as LifecycleRow[];
        const allPages: TruthTablePage[] = typedLifecycleRows.map((row) => {
          const pageHasOcr = row.ocr_done;
          const isCandidate = row.is_candidate;
          const aiScanned = row.ai_scanned;
          const aiDealExtracted = (row.ai_deals_extracted ?? 0) > 0;

          // Determine outcome
          let outcome: PageOutcome;
          let outcomeReason: string;

          if (aiDealExtracted) {
            outcome = "DEAL_CREATED";
            outcomeReason = row.ai_deal_summary || "Deal extracted";
          } else if (aiScanned && !aiDealExtracted) {
            outcome = "OCR_COMPLETE";
            outcomeReason = row.ai_error || "AI scanned, no deal found";
          } else if (isCandidate) {
            outcome = "CANDIDATE";
            outcomeReason = "Awaiting AI scan";
          } else if (pageHasOcr) {
            outcome = "OCR_COMPLETE";
            const score = Number(row.candidate_score ?? 0);
            const breakdown = row.candidate_score_breakdown as Record<string, unknown> | null;
            const reasonLabel = deriveCandidateResultLabel(false, score, signalThreshold, breakdown);
            outcomeReason = reasonLabel === "Below threshold"
              ? `Below threshold (${score} < ${signalThreshold})`
              : `${reasonLabel} (${score} >= ${signalThreshold})`;
          } else if (row.rendered) {
            outcome = "RENDERED";
            outcomeReason = "Awaiting OCR scan";
          } else {
            outcome = "PENDING";
            outcomeReason = row.outcome_reason || "Not yet processed";
          }

          // Use the row's own outcome if it's more specific (e.g. OCR_SKIPPED, OCR_FAILED)
          const dbOutcome = row.outcome as PageOutcome | undefined;
          if (dbOutcome && dbOutcome !== "PENDING" && (
            dbOutcome === "OCR_SKIPPED" || dbOutcome === "OCR_FAILED" || dbOutcome === "RENDER_FAILED" ||
            dbOutcome === "OCR_DOWNSCALED_OK" || dbOutcome === "OCR_DOWNSCALED_EMPTY" || dbOutcome === "OCR_DOWNSCALED_FAILED"
          )) {
            outcome = dbOutcome;
            outcomeReason = row.outcome_reason || outcomeReason;
          }

          const truthRow = row.page_truth_id ? truthRowById.get(row.page_truth_id) : undefined;
          const truthFlyerMerchant = truthRow?.flyerMerchant?.trim();
          const truthStagingItemId = truthRow?.stagingItemId ?? null;
          const stagingLabel = row.staging_item_id
            ? stagingLabelById.get(row.staging_item_id)
            : truthStagingItemId
              ? stagingLabelById.get(truthStagingItemId)
              : undefined;
          const rawFlyerMerchant = (row.flyer_merchant as string | null | undefined)?.trim();
          const rowRenderPath = String(row.render_storage_path ?? "").trim();
          const pathMerchantKey = rowRenderPath
            ? stagingMerchantByPath.get(rowRenderPath)
            : undefined;
          const merchantLookupKey = (
            rawFlyerMerchant &&
            rawFlyerMerchant.length > 0 &&
            rawFlyerMerchant.toLowerCase() !== "unknown"
              ? rawFlyerMerchant
              : undefined
          ) ?? (
            truthFlyerMerchant &&
            truthFlyerMerchant.length > 0 &&
            truthFlyerMerchant.toLowerCase() !== "unknown"
              ? truthFlyerMerchant
              : undefined
          ) ?? pathMerchantKey ?? stagingLabel?.merchantKey ?? stagingMerchantByPage.get(row.page_number);
          const merchantDisplayName = merchantLookupKey
            ? merchantNameByKeyRef.current.get(merchantLookupKey)
            : undefined;
          return {
            id: row.page_truth_id || `${row.batch_run_id}-${row.flyer_merchant}-${row.page_number}`,
            flyerId: row.staging_item_id || truthStagingItemId || row.batch_run_id,
            flyerMerchant: normalizeFlyerLabel(
              merchantDisplayName,
              row.flyer_merchant,
              truthFlyerMerchant,
              stagingLabel?.flyerName,
              stagingLabel?.merchantKey,
            ),
            pageNumber: row.page_number,
            rendered: row.rendered,
            renderPath: row.render_storage_path,
            renderWidth: row.render_width,
            renderHeight: row.render_height,
            ocrComplete: pageHasOcr,
            ocrText: row.ocr_text_preview,
            ocrCharCount: (row as any).ocr_char_count ?? undefined,
            ocrWordCount: (row as any).ocr_word_count ?? undefined,
            ocrConfidence: row.ocr_score,
            ocrDurationMs: row.ocr_duration_ms,
            ocrEngineUsed: row.ocr_engine_used,
            signalScore: row.candidate_score,
            signalThreshold,
            triggeredSignals: deriveTriggeredSignals(
              (row.matched_signals as string[] | null | undefined) ?? null,
              row.signal_breakdown as Record<string, unknown> | null | undefined,
            ),
            signalBreakdown: row.signal_breakdown ?? null,
            isCandidate,
            aiScanned,
            aiDealExtracted,
            aiDealSummary: row.ai_deal_summary,
            aiModel: row.ai_model_used,
            aiDurationMs: row.ai_duration_ms,
            aiRawOutput: row.ai_raw_output,
            outcome,
            outcomeReason,
            stagingDealId: aiDealExtracted ? row.staging_item_id : undefined,
          } satisfies TruthTablePage;
        });

        // Sort: Deals first, then Candidates, then OCR'd, then Rendered, then Pending
        const outcomePriority: Record<PageOutcome, number> = {
          "DEAL_CREATED": 0,
          "CANDIDATE": 1,
          "OCR_COMPLETE": 2,
          "OCR_DOWNSCALED_OK": 2,
          "OCR_SKIPPED": 3,
          "OCR_DOWNSCALED_EMPTY": 3,
          "OCR_DOWNSCALED_FAILED": 3,
          "RENDERED": 4,
          "OCR_FAILED": 5,
          "RENDER_FAILED": 6,
          "PENDING": 7,
        };

        allPages.sort((a, b) => {
          const priorityDiff = (outcomePriority[a.outcome] ?? 7) - (outcomePriority[b.outcome] ?? 7);
          if (priorityDiff !== 0) return priorityDiff;
          const merchantDiff = a.flyerMerchant.localeCompare(b.flyerMerchant);
          if (merchantDiff !== 0) return merchantDiff;
          return a.pageNumber - b.pageNumber;
        });

        setPages(allPages);
      } else if (rpcError) {
        console.error("[CR461] RPC rpc_cr460_page_lifecycle error:", rpcError);
        // Fall back to legacy fetch from cr075_flyer_page_truth directly
        await fetchTruthTableLegacy(isPolling);
      } else {
        // RPC returned empty — might be no data yet
        setPages([]);
      }
    } catch (err) {
      console.error("[CR461] Error building truth table:", err);
    } finally {
      if (!isPolling) setLoading(false);
    }
  }, [batchRunId, stepAttempts.selectedOcrAttemptId, stepAttempts.selectedAiAttemptId]);

  // Legacy fetch for backward compatibility (when RPC fails or data not yet stamped)
  const fetchTruthTableLegacy = useCallback(async (isPolling = false) => {
    try {
      const { data: stagingItems, error } = await supabase
        .from("cr075_staging_deal_items")
        .select("id, merchant_key, evidence_json, validated_payload_json")
        .eq("batch_run_id", batchRunId)
        .eq("source_type", "FLYER");

      if (error) {
        console.error("[CR461] Legacy fetch error:", error);
        return;
      }

      await hydrateMerchantDisplayNames((stagingItems ?? []).map((item) => item.merchant_key));

      const { data: ocrAttempts } = await supabase
        .from("cr075_batch_run_step_attempts")
        .select("diagnostics_json, status")
        .eq("batch_run_id", batchRunId)
        .eq("step_key", "S1_OCR")
        .order("created_at", { ascending: false })
        .limit(1);

      const ocrAttempt = ocrAttempts?.[0] as Record<string, unknown> | undefined;
      const ocrDiagnostics = ocrAttempt?.diagnostics_json as Record<string, unknown> | undefined;
      const ocrPages = Array.isArray(ocrDiagnostics?.pages) ? ocrDiagnostics.pages as Record<string, unknown>[] : [];
      const signalThreshold = (ocrDiagnostics?.signal_threshold as number) ?? 12;

      const allPages: TruthTablePage[] = [];

      for (const item of stagingItems || []) {
        const evidence = (item.evidence_json as Record<string, unknown>) ?? {};
        const payload = (item.validated_payload_json as Record<string, unknown>) ?? {};
        const extractedDeal = getValidatedDeal(payload);

        const flyerPages = Array.isArray(evidence.flyer_pages) ? evidence.flyer_pages as Record<string, unknown>[] : [];
        const ocrPagesData = Array.isArray(evidence.ocr_pages) ? evidence.ocr_pages as Record<string, unknown>[] : [];
        const candidatePages = Array.isArray(evidence.candidate_pages) ? evidence.candidate_pages as Record<string, unknown>[] : [];
        const aiDeals = Array.isArray(evidence.ai_extracted_deals) ? evidence.ai_extracted_deals as Record<string, unknown>[] : [];

        const isRendered = evidence.render_status === "RENDERED" && flyerPages.length > 0;
        const hasOcr = evidence.ocr_scan_status === "COMPLETE" || ocrPagesData.length > 0;

        if (!isRendered && flyerPages.length === 0) {
          allPages.push({
            id: `${item.id}-0`,
            flyerId: item.id,
            flyerMerchant: normalizeFlyerLabel(
              merchantNameByKeyRef.current.get(item.merchant_key),
              item.merchant_key,
            ),
            pageNumber: 0,
            rendered: false,
            ocrComplete: false,
            isCandidate: false,
            aiScanned: false,
            aiDealExtracted: false,
            outcome: "PENDING",
            outcomeReason: "PDF not yet rendered",
          });
          continue;
        }

        for (let i = 0; i < flyerPages.length; i++) {
          const pageData = flyerPages[i] as { page_number?: number; storage_path?: string; width?: number; height?: number };
          const pageNum = pageData.page_number ?? (i + 1);

          const ocrPageData = ocrPagesData.find((op) => (op?.page_number as number) === pageNum);
          const candidatePage = candidatePages.find((cp) => (cp?.page_number as number) === pageNum);
          const diagPage = ocrPages.find((dp) =>
            (dp?.flyer_merchant as string) === item.merchant_key && (dp?.page_number as number) === pageNum
          ) as Record<string, unknown> | undefined;
          const aiDeal = aiDeals.find((ad) => (ad?.page_number as number) === pageNum);

          const isCandidate = !!candidatePage;
          const aiScanned = !!aiDeal;
          const aiDealExtracted = !!aiDeal && !!(aiDeal?.deal_type);
          const pageHasOcr = !!ocrPageData || !!diagPage;

          const ocrText = (ocrPageData?.ocr_text_preview ?? ocrPageData?.text ?? diagPage?.ocr_text_preview) as string | undefined;
          const ocrConfidence = (ocrPageData?.ocr_score ?? ocrPageData?.confidence ?? diagPage?.ocr_confidence) as number | undefined;

          let outcome: PageOutcome;
          let outcomeReason: string;

          if (aiDealExtracted) {
            outcome = "DEAL_CREATED";
            outcomeReason = String(aiDeal?.deal_summary || aiDeal?.deal_type || "Deal extracted");
          } else if (aiScanned) {
            outcome = "OCR_COMPLETE";
            outcomeReason = "AI scanned, no deal found";
          } else if (isCandidate) {
            outcome = "CANDIDATE";
            outcomeReason = "Awaiting AI scan";
          } else if (pageHasOcr) {
            outcome = "OCR_COMPLETE";
            const score = (ocrPageData?.signal_score ?? candidatePage?.score ?? diagPage?.signal_score ?? 0) as number;
            const breakdown =
              ((ocrPageData as Record<string, unknown> | undefined)?.candidate_score_breakdown
                ?? (candidatePage as Record<string, unknown> | undefined)?.candidate_score_breakdown
                ?? (diagPage as Record<string, unknown> | undefined)?.candidate_score_breakdown
                ?? null) as Record<string, unknown> | null;
            const reasonLabel = deriveCandidateResultLabel(false, score, signalThreshold, breakdown);
            outcomeReason = reasonLabel === "Below threshold"
              ? `Below threshold (${score} < ${signalThreshold})`
              : `${reasonLabel} (${score} >= ${signalThreshold})`;
          } else if (hasOcr) {
            outcome = "OCR_SKIPPED";
            outcomeReason = "Page not processed during OCR batch";
          } else {
            outcome = "RENDERED";
            outcomeReason = "Awaiting OCR scan";
          }

          allPages.push({
            id: `${item.id}-${pageNum}`,
            flyerId: item.id,
            flyerMerchant: normalizeFlyerLabel(
              merchantNameByKeyRef.current.get(item.merchant_key),
              item.merchant_key,
            ),
            pageNumber: pageNum,
            rendered: true,
            renderPath: pageData.storage_path,
            renderWidth: pageData.width,
            renderHeight: pageData.height,
            ocrComplete: pageHasOcr,
            ocrText,
            ocrConfidence,
            signalScore: (ocrPageData?.signal_score ?? candidatePage?.score ?? diagPage?.signal_score) as number | undefined,
            signalThreshold,
            triggeredSignals: deriveTriggeredSignals(
              (ocrPageData?.matched_signals ?? candidatePage?.matched_signals ?? diagPage?.triggered_signals) as string[] | null | undefined,
              ((ocrPageData as Record<string, unknown> | undefined)?.candidate_score_breakdown
                ?? (candidatePage as Record<string, unknown> | undefined)?.candidate_score_breakdown
                ?? (diagPage as Record<string, unknown> | undefined)?.candidate_score_breakdown
                ?? null) as Record<string, unknown> | null | undefined,
            ),
            signalBreakdown:
              ((ocrPageData as Record<string, unknown> | undefined)?.candidate_score_breakdown
                ?? (candidatePage as Record<string, unknown> | undefined)?.candidate_score_breakdown
                ?? (diagPage as Record<string, unknown> | undefined)?.candidate_score_breakdown
                ?? null) as unknown,
            isCandidate,
            aiScanned,
            aiDealExtracted,
            aiDealSummary: (aiDeal?.deal_summary ?? aiDeal?.headline) as string | undefined,
            aiModel: ((aiDeal as Record<string, unknown>)?.model_used ?? (diagPage as Record<string, unknown> | undefined)?.ai_result) as string | undefined,
            outcome,
            outcomeReason,
            stagingDealId: aiDealExtracted ? item.id : undefined,
          });
        }
      }

      allPages.sort((a, b) => {
        const outcomePriority: Record<string, number> = {
          "DEAL_CREATED": 0, "CANDIDATE": 1, "OCR_COMPLETE": 2, "OCR_SKIPPED": 3,
          "RENDERED": 4, "OCR_FAILED": 5, "RENDER_FAILED": 6, "PENDING": 7,
        };
        const priorityDiff = (outcomePriority[a.outcome] ?? 7) - (outcomePriority[b.outcome] ?? 7);
        if (priorityDiff !== 0) return priorityDiff;
        const merchantDiff = a.flyerMerchant.localeCompare(b.flyerMerchant);
        if (merchantDiff !== 0) return merchantDiff;
        return a.pageNumber - b.pageNumber;
      });

      setPages(allPages);
    } catch (err) {
      console.error("[CR461] Legacy fetch error:", err);
    }
  }, [batchRunId]);

  // Initial fetch
  useEffect(() => {
    fetchTruthTable();
  }, [fetchTruthTable]);

  // Poll while OCR is running
  useEffect(() => {
    const isRunning = ocrStepStatus === "RUNNING" || ocrProgress?.status === "RUNNING";

    if (isRunning) {
      // Poll every 2 seconds
      pollIntervalRef.current = setInterval(() => {
        fetchTruthTable(true);
      }, 2000);
    } else {
      // Clear interval when not running
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [ocrStepStatus, ocrProgress?.status, fetchTruthTable]);

  // CR457/CR458: Derive header pill values from row data when configSnapshot is missing/incomplete.
  // CR458: Min OCR must never be derived from signal score (ocrConfidence = ocr_score = signal score).
  const derivedMeta = useMemo(() => {
    if (pages.length === 0) return null;

    // --- OCR Engine ---
    // Collect mode of non-null engine values across rows.
    // ocrEngineUsed is the only reliable mapped field (from ocr_engine_used in DB rows).
    // CR459: Normalize keys to UPPER_UNDERSCORE before counting so paddle_ocr and PADDLE_OCR
    // don't split the vote — the canonical label lookup already covers all known variants.
    /** Normalize raw engine string → canonical UPPER_UNDERSCORE key */
    const normalizeEngine = (raw: string): string => raw.trim().toUpperCase().replace(/-/g, "_");

    const engineCounts: Record<string, number> = {};
    for (const p of pages) {
      const eng = p.ocrEngineUsed; // mapped from row.ocr_engine_used by fetchTruthTable
      if (eng) {
        const key = normalizeEngine(eng);
        engineCounts[key] = (engineCounts[key] ?? 0) + 1;
      }
    }
    const topEngine = Object.entries(engineCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
    const derivedEngine = topEngine ? getOcrEngineLabel(topEngine, topEngine) : null;

    // --- Threshold ---
    // signalThreshold on TruthTablePage is the candidate signal threshold (correct for the Threshold pill).
    let derivedThreshold: number | null = null;
    for (const p of pages) {
      if (p.signalThreshold != null && p.signalThreshold > 0) {
        derivedThreshold = p.signalThreshold;
        break;
      }
    }
    // Fallback: parse from outcomeReason strings like "Below threshold (3 < 7)"
    if (derivedThreshold == null) {
      for (const p of pages) {
        const m = p.outcomeReason?.match(/Below threshold\s*\(\d+\s*<\s*(\d+)\)/i);
        if (m) {
          derivedThreshold = Number(m[1]);
          break;
        }
      }
    }

    // --- Min OCR confidence ---
    // CR458: ocrConfidence on TruthTablePage is actually mapped from ocr_score (signal score), NOT
    // real OCR confidence. We must NOT use it here. We have no reliable per-row OCR confidence field
    // in the current row shape, so we intentionally return null and let configSnapshot.min_ocr_score
    // be the sole source (handled in the rendering section below).
    const minOcrConfidence: null = null;

    return { derivedEngine, derivedThreshold, minOcrConfidence };
  }, [pages]);

  // Calculate summary
  // CR465: Added ocrChars aggregate
  const summary = useMemo(() => {
    const uniqueFlyers = new Set(pages.map(p => p.flyerId));
    // CR466: Compute aggregate OCR character count from ocr_char_count (truth), fallback to preview length
    let ocrChars = 0;
    const flyerOcrChars: Record<string, number> = {};
    for (const p of pages) {
      const pc = p.ocrCharCount ?? (p.ocrText ?? '').length;
      ocrChars += pc;
      flyerOcrChars[p.flyerMerchant] = (flyerOcrChars[p.flyerMerchant] ?? 0) + pc;
    }
    return {
      totalFlyers: uniqueFlyers.size,
      totalPages: pages.length,
      ocrPages: pages.filter(p => p.ocrComplete).length,
      candidatePages: pages.filter(p => p.isCandidate).length,
      aiCalls: pages.filter(p => p.aiScanned).length,
      dealsCreated: pages.filter(p => p.outcome === "DEAL_CREATED").length,
      discardedPages: pages.filter(p =>
        p.outcome === "OCR_COMPLETE" || p.outcome === "OCR_FAILED"
      ).length,
      ocrChars,
      flyerOcrChars,
    };
  }, [pages]);

  const outcomeCounts = useMemo(() => {
    const counts: Partial<Record<PageOutcome, number>> = {};
    for (const page of pages) {
      counts[page.outcome] = (counts[page.outcome] ?? 0) + 1;
    }
    return counts;
  }, [pages]);

  const failedOutcomes = useMemo<PageOutcome[]>(
    () => ["OCR_FAILED", "RENDER_FAILED", "OCR_DOWNSCALED_FAILED"],
    []
  );

  const failedCount = useMemo(
    () => failedOutcomes.reduce((sum, outcome) => sum + (outcomeCounts[outcome] ?? 0), 0),
    [failedOutcomes, outcomeCounts]
  );

  // Filter pages
  const filteredPages = useMemo(() => {
    if (outcomeFilter === DEFAULT_OUTCOME_FILTER) {
      return pages.filter((p) => p.ocrComplete);
    }
    if (outcomeFilter === "all") return pages;
    if (outcomeFilter === "FAILED") {
      return pages.filter((p) => failedOutcomes.includes(p.outcome));
    }
    return pages.filter(p => p.outcome === outcomeFilter);
  }, [pages, outcomeFilter, failedOutcomes]);

  const reviewQueuePages = useMemo(
    () => pages.filter((p) => p.isCandidate && !p.aiScanned && !manualLabels[p.id]),
    [pages, manualLabels]
  );

  const learningStats = useMemo(() => {
    const evaluated = pages.filter((p) => {
      if (manualLabels[p.id]) return true;
      return p.aiScanned;
    });
    let tp = 0;
    let fp = 0;
    let fn = 0;
    for (const page of evaluated) {
      const predictedPos = page.isCandidate;
      const actualPos =
        manualLabels[page.id] === "CANDIDATE" ||
        (manualLabels[page.id] == null && page.aiDealExtracted);
      if (predictedPos && actualPos) tp += 1;
      else if (predictedPos && !actualPos) fp += 1;
      else if (!predictedPos && actualPos) fn += 1;
    }
    const precision = tp + fp > 0 ? tp / (tp + fp) : null;
    const recall = tp + fn > 0 ? tp / (tp + fn) : null;

    const labeledForThreshold = evaluated.filter((p) => p.signalScore != null);
    const candidateThresholds = Array.from(
      new Set(labeledForThreshold.map((p) => Math.round(p.signalScore ?? 0)))
    )
      .filter((t) => Number.isFinite(t))
      .sort((a, b) => a - b);

    let suggestedThreshold: number | null = null;
    let bestF1 = -1;
    for (const thresholdCandidate of candidateThresholds) {
      let candTp = 0;
      let candFp = 0;
      let candFn = 0;
      for (const page of labeledForThreshold) {
        const actualPos =
          manualLabels[page.id] === "CANDIDATE" ||
          (manualLabels[page.id] == null && page.aiDealExtracted);
        const predictedPos =
          (page.signalScore ?? 0) >= thresholdCandidate &&
          hasAnchorText(page) &&
          hasOfferStructureText(page);

        if (predictedPos && actualPos) candTp += 1;
        else if (predictedPos && !actualPos) candFp += 1;
        else if (!predictedPos && actualPos) candFn += 1;
      }
      const candPrecision = candTp + candFp > 0 ? candTp / (candTp + candFp) : 0;
      const candRecall = candTp + candFn > 0 ? candTp / (candTp + candFn) : 0;
      const candF1 =
        candPrecision + candRecall > 0
          ? (2 * candPrecision * candRecall) / (candPrecision + candRecall)
          : 0;

      if (candF1 > bestF1) {
        bestF1 = candF1;
        suggestedThreshold = thresholdCandidate;
      }
    }

    return {
      modelVersion: "hybrid-v1",
      threshold: pages.find((p) => p.signalThreshold != null)?.signalThreshold ?? 12,
      reviewed: evaluated.length,
      queueCount: reviewQueuePages.length,
      precision,
      recall,
      suggestedThreshold,
    };
  }, [pages, manualLabels, reviewQueuePages.length, hasAnchorText, hasOfferStructureText]);

  const handleRowClick = (page: TruthTablePage) => {
    setSelectedPage(page);
    setEvidencePanelOpen(true);
  };

  const handleNavigateToStaging = (stagingId: string) => {
    navigate(`/admin/batch-runs/${batchRunId}?tab=staging&highlight=${stagingId}`);
  };

  const getOutcomeIcon = (outcome: PageOutcome) => {
    switch (outcome) {
      case "DEAL_CREATED":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "CANDIDATE":
        return <Zap className="h-4 w-4 text-purple-500" />;
      case "OCR_COMPLETE":
        return <Search className="h-4 w-4 text-muted-foreground" />;
      case "OCR_SKIPPED":
        return <AlertCircle className="h-4 w-4 text-amber-500" />;
      case "RENDERED":
        return <Image className="h-4 w-4 text-blue-500" />;
      case "OCR_FAILED":
      case "RENDER_FAILED":
        return <XCircle className="h-4 w-4 text-destructive" />;
      default:
        return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getOutcomeBadge = (outcome: PageOutcome) => {
    switch (outcome) {
      case "DEAL_CREATED":
        return <Badge className="bg-green-600 text-xs">Deal</Badge>;
      case "CANDIDATE":
        return <Badge variant="secondary" className="text-xs">Candidate</Badge>;
      case "OCR_COMPLETE":
        return <Badge variant="outline" className="text-xs">Dropped</Badge>;
      case "OCR_SKIPPED":
        return <Badge variant="outline" className="text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300">Skipped</Badge>;
      case "RENDERED":
        return <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-900/20">Rendered</Badge>;
      case "OCR_FAILED":
        return <Badge variant="destructive" className="text-xs">OCR Failed</Badge>;
      case "RENDER_FAILED":
        return <Badge variant="destructive" className="text-xs">Render Failed</Badge>;
      default:
        return <Badge variant="outline" className="text-xs">Pending</Badge>;
    }
  };

  const outcomeFilterOptions = useMemo(() => {
    const ocrCompleteCount = pages.filter((p) => p.ocrComplete).length;
    const options: Array<{ value: string; label: string }> = [
      { value: DEFAULT_OUTCOME_FILTER, label: `OCR Complete (${ocrCompleteCount})` },
      { value: "all", label: `All Pages (${pages.length})` },
    ];

    const appendIfAny = (value: string, label: string, count: number) => {
      if (count > 0) {
        options.push({ value, label: `${label} (${count})` });
      }
    };

    if (!hideAiReferences) appendIfAny("DEAL_CREATED", "Deals", outcomeCounts.DEAL_CREATED ?? 0);
    appendIfAny("CANDIDATE", "Candidates", outcomeCounts.CANDIDATE ?? 0);
    appendIfAny("OCR_COMPLETE", "Dropped", outcomeCounts.OCR_COMPLETE ?? 0);
    appendIfAny("FAILED", "Failed", failedCount);
    appendIfAny("OCR_FAILED", "OCR Failed", outcomeCounts.OCR_FAILED ?? 0);
    appendIfAny("RENDER_FAILED", "Render Failed", outcomeCounts.RENDER_FAILED ?? 0);
    appendIfAny("OCR_DOWNSCALED_FAILED", "OCR Downscaled Failed", outcomeCounts.OCR_DOWNSCALED_FAILED ?? 0);
    appendIfAny("OCR_SKIPPED", "OCR Skipped", outcomeCounts.OCR_SKIPPED ?? 0);
    appendIfAny("OCR_DOWNSCALED_OK", "OCR Downscaled OK", outcomeCounts.OCR_DOWNSCALED_OK ?? 0);
    appendIfAny("OCR_DOWNSCALED_EMPTY", "OCR Downscaled Empty", outcomeCounts.OCR_DOWNSCALED_EMPTY ?? 0);
    appendIfAny("RENDERED", "Rendered Only", outcomeCounts.RENDERED ?? 0);
    appendIfAny("PENDING", "Pending", outcomeCounts.PENDING ?? 0);

    return options;
  }, [pages.length, hideAiReferences, outcomeCounts, failedCount]);

  // If the active filter disappears (e.g. count drops to 0), reset safely.
  useEffect(() => {
    if (!outcomeFilterOptions.some(option => option.value === outcomeFilter)) {
      setOutcomeFilter(DEFAULT_OUTCOME_FILTER);
    }
  }, [outcomeFilter, outcomeFilterOptions]);

  if (loading) {
    if (embedded) {
      return (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      );
    }
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Signal Scan Table
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (pages.length === 0) {
    if (embedded) {
      return (
        <div className="text-center py-6 text-muted-foreground">
          <FileText className="h-6 w-6 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No flyer pages yet</p>
          <p className="text-xs mt-1">Run the flyer pipeline to see results</p>
        </div>
      );
    }
    return (
      <Card>
        <CardHeader className="pb-3">
             <CardTitle className="text-base flex items-center gap-2">
               <BarChart3 className="h-4 w-4" />
               Signal Scan Table
          </CardTitle>
          <CardDescription>
            Terminal view showing every page's outcome
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No flyer pages yet</p>
            <p className="text-xs mt-1">Run the flyer pipeline (1.0 → 1.4) to see results</p>
            {/* CR095: Show render skip/block reason */}
            {renderStepStatus.status === "SKIPPED" && (renderStepStatus.skippedReason || renderStepStatus.blockedReason) && (
              <div className="mt-4 p-3 bg-muted rounded-md text-left max-w-md mx-auto">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span className="text-xs">
                    {renderStepStatus.skippedReason || renderStepStatus.blockedReason}
                  </span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // --- Embedded mode: render without Card wrapper ---
  if (embedded) {
    return (
      <>
        <div className="space-y-3">
          {/* OCR Progress Banner — only if not hidden */}
          {!hideProgress && ocrProgress && ocrProgress.status === "RUNNING" && (
            <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-blue-800 dark:text-blue-200">OCR Processing...</span>
                  <span className="text-sm text-blue-600 dark:text-blue-300">{ocrProgress.processed} / {ocrProgress.total} pages</span>
                </div>
                <Progress value={ocrProgress.total > 0 ? (ocrProgress.processed / ocrProgress.total) * 100 : 0} className="h-2" />
              </div>
            </div>
          )}

          {/* Data Source Banner */}
          {!hideDataSourceBanner && (
            <DataSourceBanner
              ocrAttempts={stepAttempts.ocrAttempts}
              aiAttempts={stepAttempts.aiAttempts}
              selectedOcrAttemptId={stepAttempts.selectedOcrAttemptId}
              selectedAiAttemptId={stepAttempts.selectedAiAttemptId}
              syncStatus={stepAttempts.syncStatus}
              onSelectOcrAttempt={stepAttempts.selectOcrAttempt}
              onSelectAiAttempt={stepAttempts.selectAiAttempt}
            />
          )}

          <Separator />

          <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <BrainCircuit className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Learning Assistant</span>
                <Badge variant="outline" className="text-[10px]">Model: {learningStats.modelVersion}</Badge>
                <Badge variant="outline" className="text-[10px]">Threshold: {learningStats.threshold}</Badge>
              </div>
              <Dialog open={reviewQueueOpen} onOpenChange={setReviewQueueOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="h-7 text-xs">
                    <Target className="h-3 w-3 mr-1" />
                    Review Queue ({learningStats.queueCount})
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl">
                  <DialogHeader>
                    <DialogTitle>Teach The Model</DialogTitle>
                    <DialogDescription>
                      Review unscanned candidate pages and label them to calibrate candidate behavior over time.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="max-h-[55vh] overflow-auto border rounded-md">
                    <Table>
                      <TableHeader className="sticky top-0 bg-background z-10">
                        <TableRow>
                          <TableHead>Flyer</TableHead>
                          <TableHead className="text-center">Page</TableHead>
                          <TableHead className="text-center">Score</TableHead>
                          <TableHead className="text-center">Heuristic</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reviewQueuePages.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">
                              No pages in review queue.
                            </TableCell>
                          </TableRow>
                        ) : reviewQueuePages.map((page) => (
                          <TableRow key={`queue-${page.id}`}>
                            <TableCell className="font-medium">{page.flyerMerchant}</TableCell>
                            <TableCell className="text-center">{page.pageNumber}</TableCell>
                            <TableCell className="text-center font-mono">{page.signalScore?.toFixed(2) ?? "—"}</TableCell>
                            <TableCell className="text-center">{Math.round(getPageProbability(page) * 100)}%</TableCell>
                            <TableCell className="space-x-2">
                              <Button size="sm" variant="outline" onClick={() => handleRowClick(page)}>
                                <Eye className="h-3 w-3 mr-1" />
                                Inspect
                              </Button>
                              <Button size="sm" onClick={() => setManualLabel(page.id, "CANDIDATE")}>
                                Mark Candidate
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => setManualLabel(page.id, "NOT_CANDIDATE")}>
                                Mark Not Candidate
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <Badge variant="secondary">Reviewed: {learningStats.reviewed}</Badge>
              <Badge variant="secondary">
                Precision: {learningStats.precision == null ? "—" : `${Math.round(learningStats.precision * 100)}%`}
              </Badge>
              <Badge variant="secondary">
                Recall: {learningStats.recall == null ? "—" : `${Math.round(learningStats.recall * 100)}%`}
              </Badge>
              <Badge variant="secondary">
                Suggested threshold: {learningStats.suggestedThreshold ?? "—"}
              </Badge>
              <Badge variant="secondary">Unreviewed queue: {learningStats.queueCount}</Badge>
            </div>
          </div>

          {/* Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={outcomeFilter} onValueChange={setOutcomeFilter}>
              <SelectTrigger className="w-40 h-8 text-sm">
                <SelectValue placeholder="Filter by outcome" />
              </SelectTrigger>
              <SelectContent>
                {outcomeFilterOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">
              Showing {filteredPages.length} of {pages.length} pages
            </span>
          </div>

          {/* Truth Table — max-height with internal scroll & sticky header */}
          <div className="border rounded-lg overflow-auto" style={{ maxHeight: '60vh' }}>
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead className="w-[140px]">Flyer</TableHead>
                  <TableHead className="w-[60px] text-center">Page</TableHead>
                  <TableHead className="w-[50px] text-center">Rendered</TableHead>
                  <TableHead className="w-[50px] text-center">OCR</TableHead>
                  <TableHead className="w-[80px] text-center">Score</TableHead>
                  <TableHead className="w-[90px] text-center">Heuristic</TableHead>
                  <TableHead className="w-[90px] text-center">Source</TableHead>
                  <TableHead className="w-[50px] text-center">Candidate</TableHead>
                  {!hideAiReferences && <TableHead className="w-[50px] text-center">AI</TableHead>}
                  <TableHead className="w-[90px]">Outcome</TableHead>
                  <TableHead className="min-w-[180px]">Reason</TableHead>
                  <TableHead className="w-[220px]">Review</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPages.map((page) => (
                  <TableRow
                    key={page.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleRowClick(page)}
                  >
                    <TableCell className="font-medium text-sm">{page.flyerMerchant}</TableCell>
                    <TableCell className="text-center text-sm">{page.pageNumber || "—"}</TableCell>
                    <TableCell className="text-center">
                      {page.rendered ? <CheckCircle className="h-4 w-4 text-green-500 mx-auto" /> : <XCircle className="h-4 w-4 text-muted-foreground mx-auto" />}
                    </TableCell>
                    <TableCell className="text-center">
                      {page.ocrComplete ? <CheckCircle className="h-4 w-4 text-green-500 mx-auto" /> : <XCircle className="h-4 w-4 text-muted-foreground mx-auto" />}
                    </TableCell>
                    <TableCell className="text-center text-sm font-mono">{page.signalScore?.toFixed(2) ?? "—"}</TableCell>
                    <TableCell className="text-center text-sm">{Math.round(getPageProbability(page) * 100)}%</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="text-[10px]">{getDecisionSource(page)}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {page.isCandidate ? <CheckCircle className="h-4 w-4 text-green-500 mx-auto" /> : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    {!hideAiReferences && (
                      <TableCell className="text-center">
                        {page.aiScanned ? <CheckCircle className="h-4 w-4 text-green-500 mx-auto" /> : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                    )}
                    <TableCell>{getOutcomeBadge(page.outcome)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[260px]">
                      <div className="truncate">{page.outcomeReason}</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {getTopReasons(page).map((reason) => (
                          <Badge key={`${page.id}-${reason}`} variant="outline" className="text-[10px]">{reason}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 flex-wrap">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); handleRowClick(page); }}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant={manualLabels[page.id] === "CANDIDATE" ? "default" : "outline"} className="h-7 text-[10px] px-2" onClick={(e) => { e.stopPropagation(); setManualLabel(page.id, "CANDIDATE"); }}>
                          Candidate
                        </Button>
                        <Button size="sm" variant={manualLabels[page.id] === "NOT_CANDIDATE" ? "destructive" : "outline"} className="h-7 text-[10px] px-2" onClick={(e) => { e.stopPropagation(); setManualLabel(page.id, "NOT_CANDIDATE"); }}>
                          Not
                        </Button>
                        {manualLabels[page.id] && (
                          <Button size="sm" variant="ghost" className="h-7 text-[10px] px-2" onClick={(e) => { e.stopPropagation(); clearManualLabel(page.id); }}>
                            Clear
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Evidence Panel */}
        <FlyerEvidencePanel
          page={selectedPage}
          open={evidencePanelOpen}
          onOpenChange={setEvidencePanelOpen}
          onNavigateToStaging={handleNavigateToStaging}
        />
      </>
    );
  }

  // --- Standard (non-embedded) mode ---
  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
               <CardTitle className="text-base flex items-center gap-2">
                 <BarChart3 className="h-4 w-4" />
                 Signal Scan Table
              </CardTitle>
              <CardDescription>
                Read-only terminal view — every page's lifecycle outcome
              </CardDescription>
            </div>
            {/* CR081/CR457: Config Snapshot Chips — prefer configSnapshot, fall back to row-derived values */}
            {(configSnapshot || derivedMeta) && (() => {
              // Resolve best available values: configSnapshot first, derivedMeta as fallback.
              const rawEngine = configSnapshot?.ocr_engine_used;
              const resolvedEngine = rawEngine
                ? getOcrEngineLabel(rawEngine, rawEngine)
                : derivedMeta?.derivedEngine ?? null;

              const resolvedThreshold = configSnapshot?.candidate_threshold ?? derivedMeta?.derivedThreshold ?? null;
              // CR458: Min OCR only from configSnapshot (never from signal score)
              const resolvedMinOcr = configSnapshot?.min_ocr_score ?? null;
              const resolvedSignals = configSnapshot?.signals_count ?? null;

              return (
                <TooltipProvider>
                  <div className="flex flex-wrap gap-1.5 items-center">
                    {/* OCR Engine pill — hide if no value */}
                    {resolvedEngine && (
                      <Badge
                        variant="outline"
                        className={`text-xs ${configSnapshot?.fallback_used ? "bg-amber-50 dark:bg-amber-900/20 border-amber-300 text-amber-700 dark:text-amber-400" : ""}`}
                      >
                        OCR: {resolvedEngine}{configSnapshot?.fallback_used ? " (fallback)" : ""}
                      </Badge>
                    )}

                    {/* Fallback warning */}
                    {configSnapshot?.fallback_used && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <AlertCircle className="h-4 w-4 text-amber-500 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="text-xs"><strong>Fallback used:</strong> {configSnapshot.fallback_reason || "Unknown reason"}</p>
                          <p className="text-xs mt-1">Selected: {configSnapshot.ocr_engine_selected} → Used: {configSnapshot.ocr_engine_used}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {/* Execution mode */}
                    {configSnapshot?.execution_mode && (
                      <Badge
                        variant="outline"
                        className={`text-xs ${configSnapshot.execution_mode === "CLIENT" ? "bg-green-50 dark:bg-green-900/20 border-green-300 text-green-700 dark:text-green-400" : "bg-blue-50 dark:bg-blue-900/20 border-blue-300 text-blue-700 dark:text-blue-400"}`}
                      >
                        {configSnapshot.execution_mode === "CLIENT" ? "🖥️ Browser" : "☁️ Edge"}
                      </Badge>
                    )}

                    {/* Threshold pill — hide if unknown */}
                    {resolvedThreshold != null && (
                      <Badge variant="outline" className="text-xs">
                        Threshold: {resolvedThreshold}
                      </Badge>
                    )}

                    {/* Min OCR pill — hide if unknown */}
                    {resolvedMinOcr != null && (
                      <Badge variant="outline" className="text-xs">
                        Min OCR: {typeof resolvedMinOcr === "number" && resolvedMinOcr <= 1
                          ? `${Math.round(resolvedMinOcr * 100)}%`
                          : resolvedMinOcr}
                      </Badge>
                    )}

                    {/* Signals pill — only show if we have a real count from configSnapshot */}
                    {resolvedSignals != null && (
                      <Badge variant="outline" className="text-xs">
                        Signals: {resolvedSignals}
                      </Badge>
                    )}

                    {/* View Proof Button */}
                    <Dialog open={showProofDialog} onOpenChange={setShowProofDialog}>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs gap-1">
                          <Info className="h-3 w-3" />
                          View Proof
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg">
                        <DialogHeader>
                          <DialogTitle>OCR Run Config Snapshot</DialogTitle>
                          <DialogDescription>
                            Immutable proof of what OCR engine was used for this run
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="font-medium">Engine Selected:</div>
                            <div className="font-mono">{configSnapshot?.ocr_engine_selected}</div>

                            <div className="font-medium">Engine Used:</div>
                            <div className="font-mono">{configSnapshot?.ocr_engine_used}</div>

                            <div className="font-medium">Execution Mode:</div>
                            <div className="font-mono">{configSnapshot?.execution_mode}</div>

                            <div className="font-medium">Fallback Used:</div>
                            <div className={configSnapshot?.fallback_used ? "text-amber-600 font-medium" : ""}>
                              {configSnapshot?.fallback_used ? "Yes" : "No"}
                            </div>

                            {configSnapshot?.fallback_reason && (
                              <>
                                <div className="font-medium">Fallback Reason:</div>
                                <div className="text-amber-600">{configSnapshot?.fallback_reason}</div>
                              </>
                            )}

                            <div className="font-medium">Candidate Threshold:</div>
                            <div className="font-mono">{configSnapshot?.candidate_threshold}</div>

                            <div className="font-medium">Min OCR Score:</div>
                            <div className="font-mono">{configSnapshot?.min_ocr_score}</div>

                            <div className="font-medium">Signals Count:</div>
                            <div className={`font-mono ${configSnapshot?.signals_count === 0 ? "text-destructive font-bold" : ""}`}>
                              {configSnapshot?.signals_count}
                              {configSnapshot?.signals_count === 0 && " ⚠️ No signals!"}
                            </div>

                            {/* CR088: Show signals hash for audit */}
                            {configSnapshot?.signals_hash && (
                              <>
                                <div className="font-medium">Signals Hash:</div>
                                <div className="font-mono text-xs">{configSnapshot?.signals_hash}</div>
                              </>
                            )}

                            {/* CR088: Show signals source path */}
                            {configSnapshot?.signals_source_path && (
                              <>
                                <div className="font-medium">Signals Source:</div>
                                <div className="font-mono text-xs">{configSnapshot?.signals_source_path}</div>
                              </>
                            )}
                          </div>

                          {/* Raw JSON for advanced debugging */}
                          <details className="text-xs">
                            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                              View raw JSON
                            </summary>
                            <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto max-h-40">
                              {JSON.stringify(configSnapshot ?? {}, null, 2)}
                            </pre>
                          </details>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </TooltipProvider>
              );
            })()}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* OCR Progress Banner (while running) */}
          {ocrProgress && ocrProgress.status === "RUNNING" && (
            <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    OCR Processing...
                  </span>
                  <span className="text-sm text-blue-600 dark:text-blue-300">
                    {ocrProgress.processed} / {ocrProgress.total} pages
                  </span>
                </div>
                <Progress
                  value={ocrProgress.total > 0 ? (ocrProgress.processed / ocrProgress.total) * 100 : 0}
                  className="h-2"
                />
              </div>
              <RefreshCw className="h-4 w-4 text-blue-500 animate-pulse" />
            </div>
          )}

          {/* CR460: Data Source Banner — attempt selector + sync status */}
          {!hideDataSourceBanner && (
            <DataSourceBanner
              ocrAttempts={stepAttempts.ocrAttempts}
              aiAttempts={stepAttempts.aiAttempts}
              selectedOcrAttemptId={stepAttempts.selectedOcrAttemptId}
              selectedAiAttemptId={stepAttempts.selectedAiAttemptId}
              syncStatus={stepAttempts.syncStatus}
              onSelectOcrAttempt={stepAttempts.selectOcrAttempt}
              onSelectAiAttempt={stepAttempts.selectAiAttempt}
            />
          )}

          {/* Pipeline Outcome Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 text-sm">
            <div className="flex justify-between border rounded px-2 py-1.5 bg-muted/50">
              <span className="text-muted-foreground">Flyers</span>
              <span className="font-medium">{summary.totalFlyers}</span>
            </div>
            <div className="flex justify-between border rounded px-2 py-1.5 bg-muted/50">
              <span className="text-muted-foreground">Pages</span>
              <span className="font-medium">{summary.totalPages}</span>
            </div>
            <div className="flex justify-between border rounded px-2 py-1.5 bg-muted/50">
              <span className="text-muted-foreground">OCR'd</span>
              <span className="font-medium">{summary.ocrPages}</span>
            </div>
            <div className="flex justify-between border rounded px-2 py-1.5 bg-muted/50">
              <span className="text-muted-foreground">Candidates</span>
              <span className="font-medium">{summary.candidatePages}</span>
            </div>
            <div className="flex justify-between border rounded px-2 py-1.5 bg-muted/50">
              <span className="text-muted-foreground">AI Calls</span>
              <span className="font-medium">{summary.aiCalls}</span>
            </div>
            <div className="flex justify-between border rounded px-2 py-1.5 bg-green-50 dark:bg-green-900/20">
              <span className="text-muted-foreground">Deals</span>
              <span className="font-medium text-green-600">{summary.dealsCreated}</span>
            </div>
            <div className="flex justify-between border rounded px-2 py-1.5 bg-muted/50">
              <span className="text-muted-foreground">Discarded</span>
              <span className="font-medium">{summary.discardedPages}</span>
            </div>
            {/* CR466: Aggregate OCR character count with per-flyer tooltip */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex justify-between border rounded px-2 py-1.5 bg-muted/50 cursor-help">
                  <span className="text-muted-foreground">OCR chars</span>
                  <span className="font-medium">{summary.ocrChars.toLocaleString()}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <p className="font-medium text-xs mb-1">Per-flyer breakdown</p>
                {Object.entries(summary.flyerOcrChars).sort((a, b) => b[1] - a[1]).map(([merchant, chars]) => (
                  <div key={merchant} className="flex justify-between gap-4 text-xs">
                    <span>{merchant}</span>
                    <span className="font-mono">{chars.toLocaleString()}</span>
                  </div>
                ))}
              </TooltipContent>
            </Tooltip>
          </div>

          <Separator />

          {/* Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={outcomeFilter} onValueChange={setOutcomeFilter}>
              <SelectTrigger className="w-40 h-8 text-sm">
                <SelectValue placeholder="Filter by outcome" />
              </SelectTrigger>
              <SelectContent>
                {outcomeFilterOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">
              Showing {filteredPages.length} of {pages.length} pages
            </span>
          </div>

          {/* Truth Table */}
          <div className="border rounded-lg overflow-auto" style={{ maxHeight: '60vh' }}>
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead className="w-[140px]">Flyer</TableHead>
                  <TableHead className="w-[60px] text-center">Page</TableHead>
                  <TableHead className="w-[50px] text-center">Rendered</TableHead>
                  <TableHead className="w-[50px] text-center">OCR</TableHead>
                  <TableHead className="w-[80px] text-center">Score</TableHead>
                  <TableHead className="w-[50px] text-center">Candidate</TableHead>
                  {!hideAiReferences && <TableHead className="w-[50px] text-center">AI</TableHead>}
                  <TableHead className="w-[90px]">Outcome</TableHead>
                  <TableHead className="min-w-[150px]">Reason</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPages.map((page) => (
                  <TableRow
                    key={page.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleRowClick(page)}
                  >
                    <TableCell className="font-medium text-sm">
                      {page.flyerMerchant}
                    </TableCell>
                    <TableCell className="text-center text-sm">
                      {page.pageNumber || "—"}
                    </TableCell>
                    <TableCell className="text-center">
                      {page.rendered ? (
                        <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                      ) : (
                        <XCircle className="h-4 w-4 text-muted-foreground mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {page.ocrComplete ? (
                        <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                      ) : (
                        <XCircle className="h-4 w-4 text-muted-foreground mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-center text-sm font-mono">
                      {page.signalScore?.toFixed(2) ?? "—"}
                    </TableCell>
                    <TableCell className="text-center">
                      {page.isCandidate ? (
                        <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    {!hideAiReferences && (
                      <TableCell className="text-center">
                        {page.aiScanned ? (
                          <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      {getOutcomeBadge(page.outcome)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                      {page.outcomeReason}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Evidence Panel */}
      <FlyerEvidencePanel
        page={selectedPage}
        open={evidencePanelOpen}
        onOpenChange={setEvidencePanelOpen}
        onNavigateToStaging={handleNavigateToStaging}
      />
    </>
  );
}
